<script lang="ts">
  /**
   * ResponsiveOverlay — one overlay content, two containers (plans/mobile-v2.md §3.4).
   *
   * On desktop the caller's `desktop` snippet renders its existing container
   * (anchored popover, rail, modal…) and places the content inside it. On mobile
   * the same content renders in a `MobileSheet direction="bottom"`. The content
   * markup is written once, as the default children snippet:
   *
   *   <ResponsiveOverlay open={!!item} onClose={close} label="Item details">
   *     {#snippet desktop(content)}
   *       {#if item}<div class="my-popover" style={pos}>{@render content()}</div>{/if}
   *     {/snippet}
   *     {#if item}…shared content…{/if}
   *   </ResponsiveOverlay>
   *
   * On mobile the sheet stays mounted while closed (so it can slide in and out),
   * so content that reads nullable state must guard it itself.
   */
  import type { Snippet } from 'svelte';
  import MobileSheet, { type SheetDetent } from './MobileSheet.svelte';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    open: boolean;
    onClose: () => void;
    /** Force a container. Defaults to `$viewport.isMobile`. */
    mobile?: boolean;
    detents?: SheetDetent[];
    initialDetent?: SheetDetent;
    title?: string;
    label?: string;
    initialFocus?: 'container' | 'first';
    /** Desktop container; render the content snippet it receives inside it. */
    desktop: Snippet<[Snippet]>;
    children: Snippet;
  }

  let {
    open,
    onClose,
    mobile,
    detents,
    initialDetent,
    title,
    label,
    initialFocus,
    desktop,
    children,
  }: Props = $props();

  let isMobile = $derived(mobile ?? $viewport.isMobile);
</script>

{#if isMobile}
  <MobileSheet
    {open}
    {onClose}
    direction="bottom"
    {detents}
    {initialDetent}
    {title}
    {label}
    {initialFocus}
  >
    {@render children()}
  </MobileSheet>
{:else}
  {@render desktop(children)}
{/if}
